// File-management state for the collage app: list of saved canvases,
// which one is currently open, and CRUD actions. The actual canvas
// payload (layers, groups, etc.) is owned by useCanvas; this hook only
// tracks the file metadata + the currently-open id.

import { useCallback, useEffect, useState } from "react";
import {
  fetchCanvasList,
  fetchActiveCanvas,
  createCanvas,
  patchCanvasName,
  openCanvas,
  deleteCanvasById,
} from "../api.js";

export function useCanvasFile() {
  const [list, setList] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const canvases = await fetchCanvasList();
      setList(canvases);
      return canvases;
    } catch (err) {
      console.warn("canvas list fetch failed:", err.message);
      return [];
    }
  }, []);

  // Initial: load the most-recently-opened canvas (or auto-create one if
  // the DB has none yet).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const active = await fetchActiveCanvas();
        if (cancelled) return;
        if (active) setCurrentId(active.id);
        await refresh();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refresh]);

  const create = useCallback(
    async ({ name, payload } = {}) => {
      const created = await createCanvas({ name, payload });
      await refresh();
      setCurrentId(created.id);
      return created;
    },
    [refresh],
  );

  const switchTo = useCallback(
    async (id) => {
      try { await openCanvas(id); } catch {}
      setCurrentId(id);
      await refresh();
    },
    [refresh],
  );

  const rename = useCallback(
    async (id, name) => {
      await patchCanvasName(id, name);
      await refresh();
    },
    [refresh],
  );

  const remove = useCallback(
    async (id) => {
      await deleteCanvasById(id);
      const fresh = await refresh();
      if (id === currentId) {
        if (fresh.length > 0) {
          setCurrentId(fresh[0].id);
          try { await openCanvas(fresh[0].id); } catch {}
        } else {
          // No canvases left — create a default one so the editor isn't blank.
          const created = await createCanvas({ name: "Untitled", payload: {} });
          await refresh();
          setCurrentId(created.id);
        }
      }
    },
    [refresh, currentId],
  );

  const current = list.find((c) => c.id === currentId) || null;

  return { list, currentId, current, loading, create, switchTo, rename, remove, refresh };
}
